import { useEffect } from 'react';

function injectSchema(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function HomeSchemas() {
  useEffect(() => {
    injectSchema('schema-org', {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "TrueFitGuide",
      "url": "https://truefitguide.com",
      "description": "Free clothing size converter and fit finder for 50+ global brands.",
      "sameAs": []
    });
    injectSchema('schema-website', {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "TrueFitGuide",
      "url": "https://truefitguide.com",
      "potentialAction": { "@type": "SearchAction", "target": "https://truefitguide.com/brands?search={search_term_string}", "query-input": "required name=search_term_string" }
    });
    injectSchema('schema-service', {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Clothing Size Converter",
      "provider": { "@type": "Organization", "name": "TrueFitGuide" },
      "description": "Free clothing size conversion tool for 50+ global brands. Convert between US, UK, EU, Japan, Korea, and Australia sizes.",
      "areaServed": "Worldwide",
      "serviceType": "Size Conversion Tool"
    });
    injectSchema('schema-howto', {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Find Your Clothing Size Online",
      "description": "Find your perfect fit across 50+ brands in 3 simple steps.",
      "step": [
        { "@type": "HowToStep", "name": "Enter Measurements", "text": "Use a tape measure to get your chest, waist, hips, or foot length in cm or inches." },
        { "@type": "HowToStep", "name": "Pick Your Brand", "text": "Select from 50+ brands including Nike, Zara, H&M, Levi's, and more." },
        { "@type": "HowToStep", "name": "Get Your Size", "text": "Instant size recommendation with regional conversions (US, UK, EU, JP, KR)." }
      ]
    });
    injectSchema('schema-faq', {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "How do I find my clothing size?", "acceptedAnswer": { "@type": "Answer", "text": "Use our Find My Size tool. Enter your body measurements and we'll recommend your size across 50+ brands instantly." }},
        { "@type": "Question", "name": "Are the size conversions accurate?", "acceptedAnswer": { "@type": "Answer", "text": "Our size charts are based on official brand sizing guides and industry standards. However, sizes can vary by style and cut. Always check the specific item's size chart." }},
        { "@type": "Question", "name": "Why do sizes differ between brands?", "acceptedAnswer": { "@type": "Answer", "text": "There's no universal sizing standard. Each brand uses different fit models, measurement systems, and target demographics. European brands tend to run smaller than US brands." }},
        { "@type": "Question", "name": "Is TrueFitGuide free?", "acceptedAnswer": { "@type": "Answer", "text": "Yes! TrueFitGuide is 100% free. We provide size charts, conversion tools, and fit recommendations at no cost." }},
        { "@type": "Question", "name": "Is this professional sizing advice?", "acceptedAnswer": { "@type": "Answer", "text": "No. TrueFitGuide provides general size guidance. Sizes are approximate. Always verify with the retailer before purchasing." }}
      ]
    });
    injectSchema('schema-speakable-home', {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "TrueFitGuide - Find Your Perfect Fit",
      "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", ".speakable-intro"] }
    });
    return () => {
      ['schema-org','schema-website','schema-service','schema-howto','schema-faq','schema-speakable-home'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);
  return null;
}

export function BrandSchemas({ brand }) {
  useEffect(() => {
    if (!brand) return;
    injectSchema('schema-brand-service', {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": `${brand.name} Size Chart`,
      "provider": { "@type": "Organization", "name": "TrueFitGuide" },
      "description": `Complete ${brand.name} size chart with international conversions. ${brand.fit_guide}`,
      "areaServed": "Worldwide"
    });
    injectSchema('schema-brand-breadcrumb', {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://truefitguide.com" },
        { "@type": "ListItem", "position": 2, "name": "Brands", "item": "https://truefitguide.com/brands" },
        { "@type": "ListItem", "position": 3, "name": brand.name, "item": `https://truefitguide.com/brand/${brand.slug}` }
      ]
    });
    injectSchema('schema-brand-speakable', {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": `${brand.name} Size Chart`,
      "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", ".speakable-intro"] }
    });
    return () => {
      ['schema-brand-service','schema-brand-breadcrumb','schema-brand-speakable'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [brand]);
  return null;
}

export function BlogSchemas({ post }) {
  useEffect(() => {
    if (!post) return;
    injectSchema('schema-article', {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.excerpt || post.content?.slice(0, 160),
      "datePublished": post.created_at,
      "dateModified": post.updated_at || post.created_at,
      "publisher": { "@type": "Organization", "name": "TrueFitGuide" },
      "mainEntityOfPage": `https://truefitguide.com/blog/${post.slug}`
    });
    injectSchema('schema-blog-breadcrumb', {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://truefitguide.com" },
        { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://truefitguide.com/blog" },
        { "@type": "ListItem", "position": 3, "name": post.title }
      ]
    });
    injectSchema('schema-blog-speakable', {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": post.title,
      "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", ".speakable-intro"] }
    });
    return () => {
      ['schema-article','schema-blog-breadcrumb','schema-blog-speakable'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [post]);
  return null;
}

export function PageSpeakable({ name }) {
  useEffect(() => {
    injectSchema('schema-page-speakable', {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": name,
      "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", ".speakable-intro"] }
    });
    return () => { const el = document.getElementById('schema-page-speakable'); if (el) el.remove(); };
  }, [name]);
  return null;
}
